import React, { useState } from "react";
import {
  Box,
  FormControl,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { SkeletonLoadingTable, VisibleContent } from "../../../../components";
import { IconGoLink } from "../../../../components/SectionGoButtons";
import {
  EMPTY_CELL,
  TASK_REPORT_STATUS,
  TASK_REPORT_STATUS_OPTIONS,
  getAddressHref,
  getComplexHref,
  getTaskReportRowLocation,
} from "../../helpers";

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];

const TableCustom = ({
  items = [],
  filter,
  totalPages,
  totalCount,
  isLoading,
  onChangeFilter,
  onRowClick,
  selectedItemId = null,
}) => {
  const classes = useStyles();

  const handleChangePage = (_event, page) => {
    onChangeFilter({ ...filter, page }, true);
  };

  const handleChangeLimit = (event) => {
    onChangeFilter({ ...filter, page: 1, limit: Number(event.target.value) }, true);
  };

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 180, pl: 1 }}>Наименование дороги</TableCell>
            <TableCell sx={{ minWidth: 140 }}>Точка фиксации</TableCell>
            <TableCell sx={{ minWidth: 220 }}>Выполнение задания</TableCell>
            <TableCell sx={{ minWidth: 220 }}>Местоположение</TableCell>
            <TableCell sx={{ minWidth: 110, pt: 0, pb: 0, lineHeight: 1.1 }}>Время<br/>начала</TableCell>
            <TableCell sx={{ minWidth: 120, pt: 0, pb: 0, lineHeight: 1.1 }}>Время<br/>окончания</TableCell>
            <TableCell sx={{ minWidth: 140 }}>Номер комплекса</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <VisibleContent visible={!isLoading}>
            {items.map((item) => {
              const isConflict = item.status === TASK_REPORT_STATUS.CONFLICT;
              const primaryDaily = item.daily_data?.[0] || null;
              const location = getTaskReportRowLocation(item);
              const rowId = item.id || `${item.task_row_id}-${item.status}`;
              const isSelected = selectedItemId != null && String(rowId) === String(selectedItemId);

              return (
                <React.Fragment key={rowId}>
                  <RowItem
                    classes={classes}
                    item={item}
                    contextItem={item}
                    location={location}
                    daily={isConflict ? null : primaryDaily}
                    conflictPlaceholders={isConflict}
                    isConflict={isConflict}
                    isSelected={isSelected}
                    onRowClick={onRowClick}
                  />
                </React.Fragment>
              );
            })}
          </VisibleContent>
          <VisibleContent visible={isLoading}>
            <SkeletonLoadingTable countRows={10} countColumns={7} />
          </VisibleContent>
        </TableBody>
      </Table>

      <Box className={classes.paginationWrap}>
        <span className={classes.paginationLabel}>
          Найдено {Number(totalCount || 0).toLocaleString("ru-RU")} записей
        </span>
        <Box className={classes.paginationRight}>
          <span className={classes.paginationLabel}>Выводить по</span>
          <FormControl size="small" className={classes.selectWrap}>
            <Select
              value={String(filter.limit)}
              onChange={handleChangeLimit}
              className={classes.selectLimit}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <MenuItem key={size} value={String(size)}>{size}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <span className={classes.paginationLabel}>
            Страница {Math.min(filter.page, Math.max(totalPages, 1))} из {Math.max(totalPages, 1)}
          </span>
          <Pagination
            page={filter.page}
            count={Math.max(totalPages, 1)}
            onChange={handleChangePage}
          />
        </Box>
      </Box>
    </>
  );
};

const CellWithGoLink = ({ href, children, classes, visible = false, GoLink = IconGoLink }) => {
  if (!children || children === EMPTY_CELL) {
    return <span className={classes.cellText}>{EMPTY_CELL}</span>;
  }

  return (
    <Box className={classes.cellLinkWrap}>
      <span className={classes.cellText}>{children}</span>
      {href ? <GoLink href={href} visible={visible} /> : null}
    </Box>
  );
};

const RowItem = ({
  classes,
  item,
  contextItem,
  location,
  daily,
  conflictPlaceholders = false,
  isConflict = false,
  isConflictChild = false,
  isSelected = false,
  onRowClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const status = item?.status || (isConflictChild ? TASK_REPORT_STATUS.DAILY_ONLY : "");
  const statusOption = TASK_REPORT_STATUS_OPTIONS.find((option) => option.value === status);
  const statusLabel = statusOption?.label || EMPTY_CELL;
  const roadName = item?.task_data?.road_name || "";
  const fixationPoint = item?.task_data?.fixation_point || "";
  const addressHref = getAddressHref(location);
  const complexHref = getComplexHref(daily);

  const startTime = conflictPlaceholders ? "" : (daily?.start_time || "");
  const endTime = conflictPlaceholders ? "" : (daily?.end_time || "");
  const complexSerial = conflictPlaceholders ? "" : (daily?.complex_serial || "");

  const rowContextItem = contextItem || item;
  const canOpenDrawer = typeof onRowClick === "function" && Boolean(rowContextItem);
  const conflictCounterValue = isConflict
    ? (Array.isArray(item?.daily_data) ? item.daily_data.length : 0)
    : null;

  return (
    <TableRow
      hover={!isConflict}
      className={clsx(
        classes.row,
        status === TASK_REPORT_STATUS.DAILY_ONLY && classes.rowDailyOnly,
        status === TASK_REPORT_STATUS.TASK_ONLY && classes.rowTaskOnly,
        (isConflict || isConflictChild) && classes.rowConflict,
        isConflict && classes.rowConflictParent,
        isSelected && classes.rowSelected,
      )}
      onClick={canOpenDrawer ? () => onRowClick(rowContextItem) : undefined}
      sx={canOpenDrawer ? { cursor: "pointer" } : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <TableCell className={classes.roadCellTd}>
        <Box className={classes.roadCell}>
          <span className={classes.cellText}>{roadName || (isConflictChild ? EMPTY_CELL : EMPTY_CELL)}</span>
          {isConflict && conflictCounterValue !== null ? (
            <span className={classes.counterPill}>{conflictCounterValue}</span>
          ) : null}
        </Box>
      </TableCell>
      <TableCell>
        <span className={classes.cellText}>{fixationPoint || EMPTY_CELL}</span>
      </TableCell>
      <TableCell>
        {statusLabel ? (
          <span
            className={clsx(
              classes.statusPill,
              status === TASK_REPORT_STATUS.MATCHED && classes.statusPillMatched,
              status === TASK_REPORT_STATUS.TASK_ONLY && classes.statusPillTaskOnly,
              status === TASK_REPORT_STATUS.DAILY_ONLY && classes.statusPillDailyOnly,
              status === TASK_REPORT_STATUS.CONFLICT && classes.statusPillConflict,
            )}
          >
            {statusLabel}
          </span>
        ) : (
          <span className={classes.cellText}>{EMPTY_CELL}</span>
        )}
      </TableCell>
      <TableCell>
        {conflictPlaceholders ? (
          <span className={classes.cellText}>{EMPTY_CELL}</span>
        ) : (
          <CellWithGoLink href={addressHref} classes={classes} visible={isHovered}>
            {location?.name || EMPTY_CELL}
          </CellWithGoLink>
        )}
      </TableCell>
      <TableCell>
        <Typography className={clsx(classes.cellText, conflictPlaceholders && classes.conflictText)}>
          {startTime || EMPTY_CELL}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className={clsx(classes.cellText, conflictPlaceholders && classes.conflictText)}>
          {endTime || EMPTY_CELL}
        </Typography>
      </TableCell>
      <TableCell>
        {conflictPlaceholders ? (
          <span className={classes.cellText}>{EMPTY_CELL}</span>
        ) : (
          <CellWithGoLink href={complexHref} classes={classes} visible={isHovered}>
            {complexSerial || EMPTY_CELL}
          </CellWithGoLink>
        )}
      </TableCell>
    </TableRow>
  );
};

const useStyles = makeStyles(() => ({
  row: {
    "&:hover": {
      backgroundColor: "#EEF2FF",
    },
  },
  rowSelected: {
    backgroundColor: "#E0E7FF !important",
    "& > .MuiTableCell-root": {
      backgroundColor: "#E0E7FF !important",
    },
    "& > .MuiTableCell-root:first-of-type": {
      position: "relative",
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        backgroundColor: "#432DD7",
        pointerEvents: "none",
        zIndex: 1,
      },
    },
    "&:hover": {
      backgroundColor: "#E0E7FF !important",
      "& > .MuiTableCell-root": {
        backgroundColor: "#E0E7FF !important",
      },
    },
  },
  rowDailyOnly: {
    backgroundColor: "#FFF7ED",
  },
  rowTaskOnly: {
    backgroundColor: "#F4F4F5",
  },
  rowConflict: {
    backgroundColor: "#FEF2F2",
  },
  rowConflictParent: {
    "&:hover": {
      backgroundColor: "#FEE2E2",
    },
  },
  roadCellTd: {
    paddingLeft: "8px !important",
  },
  roadCell: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  counterPill: {
    display: "flex",
    alignItems: "center",
    padding: "0px 8px",
    minWidth: 24,
    height: 22,
    background: "#E0E7FF",
    borderRadius: "100px",
    fontWeight: 500,
    fontSize: 12,
    lineHeight: "18px",
    color: "#432DD7",
  },
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 12px",
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.1,
  },
  statusPillMatched: {
    backgroundColor: "#DCFCE7",
    color: "#116932",
  },
  statusPillTaskOnly: {
    backgroundColor: "#FEE2E2",
    color: "#991919",
  },
  statusPillDailyOnly: {
    backgroundColor: "#FEF9C3",
    color: "#713F12",
  },
  statusPillConflict: {
    backgroundColor: "#DBEAFE",
    color: "#173DA6",
  },
  cellText: {
    fontSize: 14,
    lineHeight: "20px",
    color: "#18181B",
    wordBreak: "break-word",
  },
  conflictText: {
    color: "#B91C1C",
    fontWeight: 500,
  },
  cellLinkWrap: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  paginationWrap: {
    padding: "12px 0 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  paginationRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  paginationLabel: {
    fontSize: 14,
    color: "#71717A",
    fontWeight: 400,
    whiteSpace: "nowrap",
  },
  selectWrap: {
    minWidth: 70,
  },
  selectLimit: {
    backgroundColor: "#fff",
    height: 32,
    fontSize: 14,
  },
}));

export default TableCustom;
